import { Typenames } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { VLOCContext } from 'features/lineOfCredit/providers/VLOCContextProvider/VLOCContextProvider';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { formatCurrency } from 'shared/utils/string/currency';

import useSubmitLineOfCreditPayment from './hooks/useSubmitLineOfCreditPayment';

const ConfirmPaymentScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'confirmPayment',
  });

  const { bottom } = useSafeAreaInsets();

  const { vlocContextData } = useContext(VLOCContext);
  const [errorMessage, setErrorMessage] = useState<string>();

  const vlocRepaymentSuccessfulEvent = usePressableAnalytics({
    analyticsId: 'vlocRepaymentSuccessfulTrack',
  });

  const { executeMutation, fetching, error } = useSubmitLineOfCreditPayment();

  const submitLineOfCreditPayment = async () => {
    try {
      const result = await executeMutation({
        idempotencyId: getUuid(),
        input: {
          lineOfCreditId: vlocContextData?.lineOfCreditId,
          sourceAccountId: vlocContextData?.sourceAccount?.accountId,
          amount: {
            amount: vlocContextData?.amount,
            currency: 'USD',
          },
        },
      });

      const submitManualPaymentResult =
        result?.data?.submitManualPaymentForLineOfCredit;
      if (submitManualPaymentResult) {
        const { __typename } = submitManualPaymentResult;
        if (__typename === Typenames.ManualPaymentResponse) {
          if (submitManualPaymentResult?.outstandingBalance?.amount !== 0) {
            vlocRepaymentSuccessfulEvent();
          }
          router.dismissAll();
          router.navigate({
            pathname: '/varo-line-of-credit/repayment-success',
            params: {
              captionText:
                submitManualPaymentResult?.responseMessage?.captionText,
              headlineText:
                submitManualPaymentResult?.responseMessage?.headlineText,
              messageText:
                submitManualPaymentResult?.responseMessage?.messageText,
              noteText: submitManualPaymentResult?.responseMessage?.noteText,
              outstandingBalance:
                submitManualPaymentResult?.outstandingBalance?.amount,
            },
          });
        } else if (__typename === Typenames.ManualPaymentError) {
          setErrorMessage(submitManualPaymentResult.message);
        }
      }
    } catch {
      setErrorMessage(translatedText('exceptionalError'));
    }
  };

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const hasError = error || errorMessage;

  const goBack = () => {
    router.back();
  };

  useBackHandler(hasError ? goHome : goBack);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <Stack.Screen
          options={{
            title: translatedText('buttonTitle'),
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} />
            ),
            headerBackVisible: !hasError,
            headerShown: !hasError,
            gestureEnabled: !hasError,
          }}
        />

        <View>
          <ListItem
            primaryCopy={formatCurrency(vlocContextData?.amount)}
            secondaryCopy={translatedText('amountLabel')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
          />

          <View style={styles.dividerContainer}>
            <HairLineDivider />
          </View>

          {vlocContextData?.sourceAccount ? (
            <ListItem
              primaryCopy={`${vlocContextData.sourceAccount.nickname} • ${vlocContextData.sourceAccount.accountNumberLast4}`}
              secondaryCopy={translatedText('repayLabel')}
              lineFormat={ListItemLineFormat.LABEL}
              showsChevron={false}
            />
          ) : null}
        </View>

        <View style={styles.buttonContainer}>
          <Button
            label={translatedText('buttonTitle')}
            onPress={submitLineOfCreditPayment}
            analyticsId="vlocRepaymentSubmitted"
            customAttributes={{ amount: vlocContextData?.amount ?? '' }}
            isLoading={fetching}
          />
        </View>
        <ScreenTransitionOverlay
          isLoading={false}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            (error || errorMessage) && !fetching ? (
              <LineOfCreditErrorScreenComponent
                screenAnalyticsId="/varo-line-of-credit/confirm-payment/LineOfCreditErrorScreenComponent-confirmPaymentScreen"
                trackingAnalyticsId="confirm-payment-error-screen"
                trackingCustomAttributes={{
                  step_name: VlocStepName.VLOC_REPAYMENT_CONFIRMATION,
                }}
              />
            ) : null
          }
        />
      </ScrollView>
    </View>
  );
};
const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  dividerContainer: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default ConfirmPaymentScreen;
